// src/components/billing/InvoiceDrawer.jsx
//
// Invoice detail: side panel on desktop, bottom sheet on phones (incl. the
// Capacitor app). Same component for clients and internal admins; admins
// additionally see the Xero ID and every portal card payment for this
// invoice, with "Retry Xero" / "Mark as recorded" for any that didn't reach
// Xero.

import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { apiFetchJSON } from '../../api/apiClient';
import {
  StatusBadge, formatMoney, formatDate, formatDateTime, downloadInvoicePdf,
} from './billingShared';

const PdfPreview = lazy(() => import('./PdfPreview'));

const XERO_LABEL = {
  pending: 'Waiting to post',
  recording: 'Posting to Xero…',
  recorded: 'Recorded in Xero',
  held: 'Waiting for a person',
  failed: 'Not in Xero yet',
  skipped: 'Recorded by hand',
};

export default function InvoiceDrawer({ scope, invoiceId, onClose, onPay, onChanged }) {
  const base = scope === 'admin' ? '/api/v2/admin/billing' : '/api/v2/client/billing';
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);
  const [preview, setPreview] = useState(false);
  const pdfUrl = `${base}/invoices/${encodeURIComponent(invoiceId)}/pdf`;

  const load = useCallback(async () => {
    setError(null);
    try {
      setInv(await apiFetchJSON(`${base}/invoices/${encodeURIComponent(invoiceId)}`));
    } catch (e) {
      setError(e.message || 'Could not load this invoice');
    }
  }, [base, invoiceId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (preview) return undefined; // the preview handles Escape itself
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, preview]);

  const download = async () => {
    setBusy('pdf');
    setNotice(null);
    try {
      await downloadInvoicePdf(pdfUrl, inv?.invoice_number);
    } catch (e) {
      setNotice(e.message);
    } finally {
      setBusy(null);
    }
  };

  const adminAction = async (pi, action) => {
    let body;
    if (action === 'mark-recorded') {
      const note = window.prompt('You recorded this payment in Xero by hand? Add a note (optional):', '');
      if (note === null) return;
      body = JSON.stringify({ note });
    }
    setBusy(pi);
    setNotice(null);
    try {
      const r = await apiFetchJSON(`/api/v2/admin/billing/payments/${pi}/${action}`, { method: 'POST', body });
      if (r.xero_status === 'failed') setNotice(`Still not in Xero: ${r.xero_error}`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      setNotice(e.message);
    } finally {
      setBusy(null);
    }
  };

  const canPay = inv?.can_pay_online && scope === 'client';

  return (
    <div className="bl-drawer-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="bl-drawer" role="dialog" aria-modal="true" aria-label="Invoice details">
        <div className="bl-drawer-head">
          <div>
            <div className="ft-light-label bl-drawer-eyebrow">Invoice</div>
            <h2 className="bl-drawer-title">{inv?.invoice_number || '…'}</h2>
          </div>
          <button type="button" className="bl-icon-btn" onClick={onClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {error && <div className="ft-toolbar-error" role="alert">{error}</div>}
        {!inv && !error && <div className="bl-drawer-loading">Loading invoice…</div>}

        {inv && (
          <div className="bl-drawer-body">
            <div className="bl-balance">
              <div className="bl-balance-label">
                {inv.status === 'paid' ? 'Paid in full' : inv.status === 'processing' ? 'Payment received' : 'Balance due'}
              </div>
              <div className={`bl-balance-amount ft-mono ${inv.status === 'overdue' ? 'is-overdue' : ''}`}>
                {formatMoney(inv.status === 'paid' ? inv.total : inv.amount_due, inv.currency)}
              </div>
              <StatusBadge status={inv.status} daysOverdue={inv.days_overdue} />
            </div>

            <dl className="bl-facts">
              <div><dt>From</dt><dd>{inv.company}</dd></div>
              <div><dt>Vessel</dt><dd>{inv.site_name || inv.yacht_name || inv.contact_name || '—'}</dd></div>
              {scope === 'admin' && <div><dt>Client</dt><dd>{inv.client_name || 'No client account'}</dd></div>}
              <div><dt>Issued</dt><dd>{formatDate(inv.issue_date)}</dd></div>
              <div><dt>Due</dt><dd>{formatDate(inv.due_date)}</dd></div>
              {inv.reference && <div><dt>Reference</dt><dd>{inv.reference}</dd></div>}
              {scope === 'admin' && (
                <>
                  <div><dt>Xero contact</dt><dd>{inv.contact_name || '—'}</dd></div>
                  <div><dt>Xero status</dt><dd className="ft-mono bl-small">{inv.status_raw} · copy from {formatDateTime(inv.fetched_at)}</dd></div>
                </>
              )}
            </dl>

            {inv.line_items && inv.line_items.length > 0 && (
              <div className="bl-lines">
                {inv.line_items.map((li, i) => li.note_only ? (
                  <div className="bl-line bl-line-note" key={i}>{li.description}</div>
                ) : (
                  <div className="bl-line" key={i}>
                    <div className="bl-line-desc">
                      {li.description || 'Item'}
                      {li.quantity && li.quantity !== 1 && (
                        <span className="bl-line-qty ft-mono"> {li.quantity} × {formatMoney(li.unit_amount, inv.currency)}</span>
                      )}
                    </div>
                    <div className="bl-line-amount ft-num">{formatMoney(li.line_amount, inv.currency)}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="bl-totals">
              {inv.subtotal !== null && <div><span>Subtotal</span><span className="ft-num">{formatMoney(inv.subtotal, inv.currency)}</span></div>}
              {!!inv.total_tax && <div><span>Tax</span><span className="ft-num">{formatMoney(inv.total_tax, inv.currency)}</span></div>}
              <div className="bl-totals-strong"><span>Total</span><span className="ft-num">{formatMoney(inv.total, inv.currency)}</span></div>
              {!!inv.amount_paid && <div><span>Paid</span><span className="ft-num">− {formatMoney(inv.amount_paid, inv.currency)}</span></div>}
            </div>

            {inv.status === 'processing' && (
              <p className="bl-note">
                Your card payment of {formatMoney(inv.portal_payment?.amount, inv.portal_payment?.currency)} was
                received {formatDateTime(inv.portal_payment?.paid_at)}. This invoice will show as paid once our
                accounts are updated.
              </p>
            )}
            {inv.xero_payments?.length > 0 && (
              <div className="bl-history">
                {inv.xero_payments.map((p, i) => (
                  <div key={i}><span>Payment received {formatDate(p.date)}</span><span className="ft-num">{formatMoney(p.amount, inv.currency)}</span></div>
                ))}
              </div>
            )}

            {scope === 'admin' && inv.portal_payments?.length > 0 && (
              <div className="bl-admin-payments">
                <div className="ft-light-label bl-section-label">Card payments from the portal</div>
                {inv.portal_payments.map((p) => (
                  <div className="bl-admin-payment" key={p.payment_intent_id}>
                    <div className="bl-admin-payment-main">
                      <span className="ft-mono">{formatMoney(p.amount, p.currency)}</span>
                      <span className="bl-small">{p.stripe_status === 'succeeded' ? formatDateTime(p.paid_at) : `Card: ${p.stripe_status}`}</span>
                      <a className="bl-small ft-mono" href={`https://dashboard.stripe.com/payments/${p.payment_intent_id}`} target="_blank" rel="noreferrer">
                        {p.payment_intent_id}
                      </a>
                    </div>
                    {p.stripe_status === 'succeeded' && (
                      <div className="bl-admin-payment-xero">
                        <span className={`ft-badge ${p.xero_status === 'recorded' || p.xero_status === 'skipped' ? 'ft-badge-online' : 'ft-badge-down'}`}>
                          {XERO_LABEL[p.xero_status] || p.xero_status}
                        </span>
                        {p.xero_error && <span className="bl-small bl-error-text">{p.xero_error}</span>}
                        {['failed', 'pending', 'held'].includes(p.xero_status) && (
                          <span className="bl-inline-actions">
                            <button className="bl-btn-ghost" disabled={busy === p.payment_intent_id} onClick={() => adminAction(p.payment_intent_id, 'retry')}>Retry Xero</button>
                            <button className="bl-btn-ghost" disabled={busy === p.payment_intent_id} onClick={() => adminAction(p.payment_intent_id, 'mark-recorded')}>Mark as recorded</button>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {notice && <div className="ft-toolbar-error" role="alert">{notice}</div>}
          </div>
        )}

        {inv && (
          <div className="bl-drawer-actions">
            <button type="button" className="bl-btn-secondary" onClick={() => setPreview(true)}>
              <i className="fas fa-eye"></i> Preview
            </button>
            <button type="button" className={`bl-btn-secondary${canPay ? ' bl-btn-icon-only' : ''}`} onClick={download} disabled={busy === 'pdf'} aria-label="Download PDF">
              <i className={`fas ${busy === 'pdf' ? 'fa-circle-notch fa-spin' : 'fa-download'}`}></i>{canPay ? '' : ' Download'}
            </button>
            {canPay && (
              <button type="button" className="bl-btn-primary" onClick={() => onPay(inv)}>
                Pay {formatMoney(inv.amount_due, inv.currency)}
              </button>
            )}
          </div>
        )}
      </aside>
      {preview && (
        <Suspense fallback={null}>
          <PdfPreview url={pdfUrl} invoiceNumber={inv?.invoice_number} onClose={() => setPreview(false)} />
        </Suspense>
      )}
    </div>
  );
}